import { LogOut } from 'lucide-react'
import { useState } from 'react'

import { AddressProfileField } from '@/customer/components/member/address-profile-field'
import { EmailProfileField } from '@/customer/components/member/email-profile-field'
import { NameProfileField } from '@/customer/components/member/name-profile-field'
import { PhoneProfileField } from '@/customer/components/member/phone-profile-field'
import { RegionProfileField } from '@/customer/components/member/region-profile-field'
import { PageContainer } from '@/customer/components/page-container'
import { Button } from '@/customer/components/ui/button'
import { useDocumentTitle } from '@/customer/hooks/use-document-title'
import { authClient } from '@/customer/lib/auth'

/**
 * Portal profile page.
 *
 * A stack of `EditableField` rows for the member's identifier
 * identifiers (name, email, phone) plus the DB-only address. Each
 * row owns its own edit surface:
 *
 * - Name: inline via `NameProfileField` (DB-native via
 *   DB sync).
 * - Email / Phone: pencil links out to dedicated update pages that
 *   drive the OTP verification loop.
 * - Address: inline atomic update via `PATCH /portal/me`.
 *
 * The page itself is deliberately thin - it composes the rows and
 * nothing else. All state (draft values, auth orchestration,
 * error surfaces) lives inside each row.
 */
export function ProfilePage() {
  useDocumentTitle('Your profile')

  return (
    <PageContainer
      headline={[
        { text: 'Your', color: 'yellow' },
        { text: 'profile', color: 'blue' },
      ]}
    >
      <section className="flex flex-col">
        <NameProfileField />
        <EmailProfileField />
        <PhoneProfileField />
        <AddressProfileField />
        <RegionProfileField />
      </section>
      <SignOutButton />
    </PageContainer>
  )
}

/**
 * Ends the member's session.
 *
 * Deliberately has no `navigate()` call. `signOut()` resolving is not
 * the moment the session actually ends client-side: better-auth
 * toggles its session signal on a deferred tick (its own `setTimeout`,
 * to dodge a race), which triggers a second `GET /get-session` that
 * only *then* comes back 401 and nulls the session atom. Only at that
 * point does the `<SignedIn>` guard wrapping the portal route tree in
 * `app.tsx` flip to its `<Navigate to={routes.signIn} replace />`
 * fallback. Navigating here ourselves would race that: `/sign-in`
 * sits under `<SignedOut>`, so hitting it while the atom still holds
 * session data just bounces straight back to `/account`. The tree
 * unmounts once the guard flips, so this component never sees the
 * other side of the redirect - hence no `setIsSigningOut(false)` on
 * success. The query cache clear that used to happen here on click
 * now lives in `app.tsx`'s `QueryCacheReset`, tied to that same
 * unmount rather than to this button.
 */
function SignOutButton() {
  const [isSigningOut, setIsSigningOut] = useState(false)
  const [hasFailed, setHasFailed] = useState(false)

  async function handleSignOut() {
    setIsSigningOut(true)
    setHasFailed(false)
    // `signOut` resolves with `{ data, error }` rather than throwing,
    // so the error branch is the only thing standing between a failed
    // request and a button that silently does nothing.
    const { error } = await authClient.signOut()
    if (error) {
      setIsSigningOut(false)
      setHasFailed(true)
    }
  }

  return (
    <div className="mt-8 flex flex-col gap-2">
      <Button
        type="button"
        variant="outline"
        className="w-full"
        disabled={isSigningOut}
        onClick={() => void handleSignOut()}
      >
        <LogOut />
        {isSigningOut ? 'Signing out…' : 'Sign out'}
      </Button>
      {hasFailed ? (
        <p className="text-center text-body-sm text-destructive">
          Couldn't sign you out. Please try again.
        </p>
      ) : null}
    </div>
  )
}
